import { useCallback, useEffect, useMemo, useState } from 'react'
import { UnknownAction } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useDispatch, useSelector } from 'react-redux'

import {
    clearEntitlements,
    normalizeEntitlementsState,
    setEntitlements,
} from '../redux/features/entitlements/entitlementsSlice'
import { CurrentUser, normalizeCurrentUser } from '../../lib/currentUser'
import { getPortalState } from '../../lib/portalDomain'
import { mergeAuthCurrentCache } from '../redux/api/authCurrentCache'
import {
    enhancedApi as webApi,
    useAuthGetCurrentQuery,
    useEntitlementsDeleteContextTokenMutation,
} from '../redux/api/web-api'
import { reset, setUser } from '../redux/features/user/userSlice'
import {
    requestPortalSessionReset,
    selectPortalSessionResetRequestCount,
} from '../redux/features/portal-session-reset/portalSessionResetSlice'
import { addPortalSessionResetListener } from '../utils/portalSessionReset'
import { clearPortalSessionClientState } from '../../lib/portalSessionClientState'

import {
    buildPortalSessionSnapshot,
    canHydratePortalSession,
    getPortalSessionStatus,
    PortalSessionStatus,
} from './portalSessionState'

const emptySessionUser = normalizeCurrentUser({}) as CurrentUser

export const usePortalSession = (): PortalSessionStatus => {
    const dispatch = useDispatch()
    const portal = getPortalState(window.location.pathname)
    const dispatchApiAction = useCallback((action: unknown) => dispatch(action as UnknownAction), [dispatch])
    const sessionQuery = useAuthGetCurrentQuery()
    const [deleteEntitlementsContext] = useEntitlementsDeleteContextTokenMutation()
    const [isClearingUnexpectedContext, setIsClearingUnexpectedContext] = useState(false)
    const [hasUnexpectedContextCleanupFailed, setHasUnexpectedContextCleanupFailed] = useState(false)
    const portalSessionResetCount = useSelector(selectPortalSessionResetRequestCount)
    const sessionSnapshot = useMemo(
        () =>
            buildPortalSessionSnapshot(sessionQuery.data, {
                currentDomain: portal.currentDomain,
                currentPortalAudience: portal.currentPortalAudience,
            }),
        [portal.currentDomain, portal.currentPortalAudience, sessionQuery.data]
    )
    const {
        selectedOrganizationId,
        sessionEntitlements,
        sessionUser,
        userHasPortalAccess,
        hasUnexpectedEntitlementsContext,
    } = sessionSnapshot
    const canHydrateSession = canHydratePortalSession(sessionSnapshot, hasUnexpectedContextCleanupFailed)

    const clearHydratedPortalViewState = useCallback(() => {
        dispatch(setUser(emptySessionUser))
        dispatch(clearEntitlements())
    }, [dispatch])

    const handlePortalSessionReset = useCallback(() => {
        dispatch(requestPortalSessionReset())
    }, [dispatch])

    const applyHydratedPortalSession = useCallback(() => {
        dispatch(setUser(sessionUser as CurrentUser))
        if (sessionEntitlements || selectedOrganizationId) {
            dispatch(setEntitlements(normalizeEntitlementsState(sessionEntitlements, selectedOrganizationId)))
        } else {
            dispatch(clearEntitlements())
        }
    }, [dispatch, selectedOrganizationId, sessionEntitlements, sessionUser])

    useEffect(() => addPortalSessionResetListener(handlePortalSessionReset), [handlePortalSessionReset])

    // React owns same-tab portal-session reset by turning the shared reset signal into session state work.
    useEffect(() => {
        if (portalSessionResetCount === 0) return

        setIsClearingUnexpectedContext(false)
        setHasUnexpectedContextCleanupFailed(false)
        clearPortalSessionClientState()
        dispatchApiAction(webApi.util.resetApiState())
        dispatch(reset())
    }, [dispatch, dispatchApiAction, portalSessionResetCount])

    // Remove unexpected org-scoped context before authenticating non-admin portals.
    useEffect(() => {
        if (!sessionQuery.isSuccess || !userHasPortalAccess || !hasUnexpectedEntitlementsContext) {
            return
        }

        let isMounted = true
        setIsClearingUnexpectedContext(true)
        setHasUnexpectedContextCleanupFailed(false)

        void deleteEntitlementsContext()
            .unwrap()
            .then(() => {
                if (!isMounted) return

                dispatchApiAction(
                    webApi.util.upsertQueryData(
                        'authGetCurrent',
                        undefined,
                        mergeAuthCurrentCache(sessionQuery.data, {
                            selectedOrganizationId: null,
                            entitlements: null,
                        })
                    )
                )
                dispatch(clearEntitlements())
            })
            .catch(() => {
                if (!isMounted) return

                setHasUnexpectedContextCleanupFailed(true)
            })
            .finally(() => {
                if (!isMounted) return

                setIsClearingUnexpectedContext(false)
            })

        return () => {
            isMounted = false
        }
    }, [
        deleteEntitlementsContext,
        dispatch,
        dispatchApiAction,
        hasUnexpectedEntitlementsContext,
        selectedOrganizationId,
        sessionEntitlements,
        sessionQuery.data,
        sessionQuery.isSuccess,
        userHasPortalAccess,
    ])

    // Sync hydrated backend session data into local runtime state when the session is usable.
    useEffect(() => {
        if (!sessionQuery.isSuccess) return

        if (canHydrateSession) {
            applyHydratedPortalSession()
            return
        }

        clearHydratedPortalViewState()
    }, [applyHydratedPortalSession, canHydrateSession, clearHydratedPortalViewState, sessionQuery.isSuccess])

    // Fail closed locally on unauthorized bootstrap without trying to manipulate the shared backend session.
    // No-current-session `204` is handled separately through the successful empty-payload path.
    useEffect(() => {
        if (!sessionQuery.isError) return

        const status = (sessionQuery.error as FetchBaseQueryError).status
        if (status === 401) {
            clearHydratedPortalViewState()
        }
    }, [clearHydratedPortalViewState, sessionQuery.error, sessionQuery.isError])

    return useMemo(() => {
        return getPortalSessionStatus({
            isSuccess: sessionQuery.isSuccess,
            isLoading: sessionQuery.isLoading,
            isFetching: sessionQuery.isFetching,
            isClearingUnexpectedContext,
            canHydrateSession,
        })
    }, [
        canHydrateSession,
        isClearingUnexpectedContext,
        sessionQuery.isFetching,
        sessionQuery.isLoading,
        sessionQuery.isSuccess,
    ])
}
